import { File, XCircle } from 'react-feather';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { setServerErrors, useMutation } from '../../../Shared/api';
import { containsAddress } from '../../../Shared/helpers';
import { useLocale } from '../../../Shared/locale';
import { useNotifier } from '../../../Shared/Notifier/NotificationProvider';
import { cancellationReasons, Charity, DonationForm, DonationFrequency, donorTypes, Gender } from '../../../Shared/types';
import AddressInputs from '../../../Shared/UI/AddressInputs';
import { Button } from '../../../Shared/UI/Button';
import { Card } from '../../../Shared/UI/Card';
import { Checkbox } from '../../../Shared/UI/Checkbox';
import CheckboxLabel from '../../../Shared/UI/CheckboxLabel';
import { DateInput } from '../../../Shared/UI/DateInput';
import ErrorMessage from '../../../Shared/UI/ErrorMessage';
import { FadeIn } from '../../../Shared/UI/FadeIn';
import { Icon } from '../../../Shared/UI/Icon';
import { Input } from '../../../Shared/UI/Input';
import InputGroup from '../../../Shared/UI/InputGroup';
import InputLabel from '../../../Shared/UI/InputLabel';
import { InputWrapper } from '../../../Shared/UI/InputWrapper';
import PhoneNumberInput from '../../../Shared/UI/PhoneNumberInput';
import Picker, { PickerItem } from '../../../Shared/UI/Picker';
import { Radio } from '../../../Shared/UI/Radio';
import RadioLabel from '../../../Shared/UI/RadioLabel';
import { Select } from '../../../Shared/UI/Select';
import ServerErrors from '../../../Shared/UI/ServerErrors';
import { useCurrencyInput } from '../../../Shared/useCurrencyInput';
import { useDateInput } from '../../../Shared/useDateInput';
import { useRouter } from '../../router';
import { Subscription } from '../../types';

export default function EditSubscriptionForm({
  subscription,
  donationForms,
  charity,
}: {
  subscription: Subscription & {
    donation_form: DonationForm;
  };
  donationForms: PickerItem[];
  charity: Charity;
}) {
  const { t } = useTranslation();
  const { routes } = useRouter();
  const { defaultCountry, formatDate, parseDate } = useLocale();
  const { handleCurrencyBlur, formatCurrency, parseCurrency } = useCurrencyInput();
  const { handleDateBlur } = useDateInput();
  const notifier = useNotifier();

  const form = useForm({
    defaultValues: {
      donation_form_id: subscription.donation_form.id,
      amount: formatCurrency(subscription.amount),
      frequency: subscription.frequency,
      end_date: subscription.end_date ? formatDate(subscription.end_date, 'internal_date') : null,
      cancellation_reason: subscription.cancellation_reason,
      cancellation_description: subscription.cancellation_description,
      iban: subscription.iban,
      donor_type: subscription.donor_type,
      first_name: subscription.first_name,
      last_name: subscription.last_name,
      email: subscription.email,
      email_opt_in: subscription.email_opt_in,
      gender: subscription.gender,
      date_of_birth: subscription.date_of_birth ? formatDate(subscription.date_of_birth, 'input_date') : '',
      company_name: subscription.company_name,
      phone: subscription.phone,
      phone_opt_in: subscription.phone_opt_in,
      street: subscription.street,
      house_number: subscription.house_number,
      extra_address_line: subscription.extra_address_line,
      zip_code: subscription.zip_code,
      city: subscription.city,
      country: subscription.country || defaultCountry,
    },
  });

  const {
    register, setValue, handleSubmit, setError, watch, control,
    formState: { errors, defaultValues },
  } = form;

  const endDate = watch('end_date');

  const gender = watch('gender');

  const [editSubscription, { loading, errors: serverErrors }] = useMutation(routes['edit_subscription'](subscription.id), {
    onSuccess: () => {
      notifier.notify(t('backend:donation_saved'));
    },
    onError: (errors) => {
      setServerErrors(errors, setError, defaultValues);
    },
  });

  const submit = async (data: NonNullable<typeof defaultValues>) => {
    const dateOfBirth = data?.date_of_birth && parseDate(data.date_of_birth, 'input_date');

    await editSubscription({
      donation_form_id: data?.donation_form_id,
      amount: data?.amount ? parseCurrency(data.amount) : null,
      frequency: data?.frequency,
      end_date: data?.end_date,
      cancellation_reason: data?.cancellation_reason,
      cancellation_description: data?.cancellation_description,
      iban: data?.iban,
      donor_type: data?.donor_type,
      first_name: data?.first_name,
      last_name: data?.last_name,
      email: data?.email,
      email_opt_in: data?.email_opt_in,
      gender: data?.gender,
      date_of_birth: dateOfBirth ? formatDate(dateOfBirth, 'internal_date') : null,
      company_name: data?.company_name,
      phone: data?.phone,
      phone_opt_in: data?.phone_opt_in,
      street: data?.street,
      house_number: data?.house_number,
      extra_address_line: data?.extra_address_line,
      zip_code: data?.zip_code,
      city: data?.city,
      country: containsAddress(data) ? data?.country : null,
    });
  };

  return (
    <form onSubmit={handleSubmit(submit)}>
      <Card panels header footer>
        <div>
          <h3>
            {t('backend:edit_donation_details')}
          </h3>
        </div>
        <div className="space-y-6">
          {serverErrors && <ServerErrors errors={serverErrors} defaultValues={defaultValues} scrollIntoView />}

          <InputGroup>
            <InputLabel required valid={!errors.donation_form_id} htmlFor="subscription_donation_form">
              {t('backend:donation_form')}
            </InputLabel>
            <Controller
              control={control}
              name="donation_form_id"
              rules={{ required: true }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Picker
                  selected={value}
                  items={donationForms}
                  onChange={(value) => onChange(value?.id || null)}
                  onBlur={onBlur}
                  icon={<File />}
                  aria-invalid={!!errors.donation_form_id}
                  id="subscription_donation_form"
                />
              )}
            />
            <ErrorMessage attribute={t('backend:donation_form')} error={errors.donation_form_id} />
          </InputGroup>

          <div className="grid grid-cols-2 gap-6">
            <InputGroup>
              <InputLabel required valid={!errors.amount} htmlFor="subscription_amount">
                {t('backend:amount')}
              </InputLabel>
              <InputWrapper>
                <span>
                  €
                </span>
                <Input
                  {...register('amount', { required: true })}
                  id="subscription_amount"
                  className="pl-7"
                  onBlur={(event) => {
                    handleCurrencyBlur(event);
                    setValue('amount', event.target.value);
                  }}
                  aria-invalid={!!errors.amount}
                  min="0"
                />
              </InputWrapper>
              <ErrorMessage error={errors.amount} attribute={t('backend:amount')} />
            </InputGroup>

            <InputGroup>
              <InputLabel required valid={!errors.frequency} htmlFor="subscription_frequency">
                {t('backend:frequency')}
              </InputLabel>
              <Select
                {...register('frequency', { required: true })}
                id="subscription_frequency"
                aria-invalid={!!errors.frequency}
              >
                <option value={DonationFrequency.Monthly}>
                  {t('backend:monthly')}
                </option>
                <option value={DonationFrequency.Yearly}>
                  {t('backend:yearly')}
                </option>
              </Select>
              <ErrorMessage attribute={t('backend:frequency')} error={errors.frequency }/>
            </InputGroup>
          </div>

          <div className="grid grid-cols-2 gap-6">

            <InputGroup>
              <InputLabel required valid={!errors.iban} htmlFor="subscription_iban">
                {t('backend:iban')}
              </InputLabel>
              <Input
                {...register('iban', { required: true })}
                id="subscription_iban"
                aria-invalid={!!errors.iban}
              />
              <ErrorMessage attribute={t('backend:iban')} error={errors.iban }/>
            </InputGroup>

            <InputGroup>
              <InputLabel htmlFor="subscription_end_date">
                {t('backend:end_date')}
              </InputLabel>
              <Controller
                control={control}
                name="end_date"
                render={({ field: { onChange, onBlur, value } }) => (
                  <DateInput
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    aria-invalid={!!errors.end_date}
                    optional
                    minDate={subscription.start_date}
                    id="subscription_end_date"
                  />
                )}
              />
              <ErrorMessage
                error={errors.end_date}
                attribute={t('backend:end_date')}
                values={{ min: t('backend:start_date') }}
              />
            </InputGroup>
          </div>

          <FadeIn
            when={!!endDate}
            render={(() => (
              <div className="grid grid-cols-2 gap-6">
                <InputGroup>
                  <InputLabel valid={!errors.cancellation_reason} htmlFor="subscription_cancellation_reason">
                    {t('backend:cancellation_reason')}
                  </InputLabel>
                  <Select
                    {...register('cancellation_reason')}
                    id="subscription_cancellation_reason"
                    aria-invalid={!!errors.cancellation_reason}
                  >
                    <option value=""></option>
                    {cancellationReasons.map((reason) => (
                      <option value={reason} key={reason}>
                        {t(`backend:cancellation_reasons.${reason}`)}
                      </option>
                    ))}
                  </Select>
                  <ErrorMessage attribute={t('backend:cancellation_reason')} error={errors.cancellation_reason }/>
                </InputGroup>

                <InputGroup>
                  <InputLabel valid={!errors.cancellation_description} htmlFor="subscription_cancellation_description">
                    {t('backend:cancellation_description_optional')}
                  </InputLabel>
                  <Input
                    {...register('cancellation_description')}
                    id="subscription_cancellation_description"
                    aria-invalid={!!errors.cancellation_description}
                  />
                  <ErrorMessage attribute={t('backend:cancellation_description_optional')} error={errors.cancellation_description }/>
                </InputGroup>
              </div>
            ))}
          />

          <hr />

          <div className="grid grid-cols-2 gap-6">
            <InputGroup>
              <InputLabel required valid={!errors.first_name} htmlFor="subscription_first_name">
                {t('backend:first_name')}
              </InputLabel>
              <Input
                {...register('first_name', { required: true })}
                id="subscription_first_name"
                aria-invalid={!!errors.first_name}
              />
              <ErrorMessage attribute={t('backend:first_name')} error={errors.first_name }/>
            </InputGroup>

            <InputGroup>
              <InputLabel required valid={!errors.last_name} htmlFor="subscription_last_name">
                {t('backend:last_name')}
              </InputLabel>
              <Input
                {...register('last_name', { required: true })}
                id="subscription_last_name"
                aria-invalid={!!errors.last_name}
              />
              <ErrorMessage attribute={t('backend:last_name')} error={errors.last_name }/>
            </InputGroup>
          </div>

          <InputGroup>
            <InputLabel required valid={!errors.email} htmlFor="subscription_email">
              {t('backend:email_address')}
            </InputLabel>
            <Input
              {...register('email', { required: true })}
              inputMode="email"
              id="subscription_email"
              aria-invalid={!!errors.email}
            />
            <ErrorMessage attribute={t('backend:email_address')} error={errors.email }/>
          </InputGroup>

          <InputGroup>
            <InputLabel valid={!errors.gender} htmlFor="subscription_gender">
              {t('backend:gender')}
            </InputLabel>
            <div className="flex space-x-3">
              <RadioLabel>
                <Radio
                  {...register('gender')}
                  value={Gender.Male}
                  aria-invalid={!!errors.gender}
                />
                <span>
                  {t('shared:genders.male')}
                </span>
              </RadioLabel>
              <RadioLabel>
                <Radio
                  {...register('gender')}
                  value={Gender.Female}
                  aria-invalid={!!errors.gender}
                />
                <span>
                  {t('shared:genders.female')}
                </span>
              </RadioLabel>
              <RadioLabel>
                <Radio
                  {...register('gender')}
                  value={Gender.Nonbinary}
                  aria-invalid={!!errors.gender}
                />
                <span>
                  {t('shared:genders.nonbinary')}
                </span>
              </RadioLabel>
              {gender && (
                <Button
                  onClick={(event) => {
                    event.preventDefault(); setValue('gender', null);
                  }}
                  variant="tertiary"
                  size="xs"
                  aria-label={t('shared:filter.clear')}
                >
                  <Icon>
                    <XCircle />
                  </Icon>
                </Button>
              )}
            </div>
            <ErrorMessage error={errors.gender} attribute={t('frontend:gender')} />
          </InputGroup>

          <InputGroup>
            <InputLabel htmlFor="subscription_date_of_birth">
              {t('backend:date_of_birth')}
            </InputLabel>
            <Input
              {...register('date_of_birth')}
              onBlur={(event) => {
                handleDateBlur(event);
                setValue('date_of_birth', event.target.value);
              }}
              id="subscription_date_of_birth"
              placeholder={t('shared:date_input.date_format_placeholder')}
              aria-invalid={!!errors.date_of_birth}
            />
            <ErrorMessage error={errors.date_of_birth} attribute={t('backend:date_of_birth')} />
          </InputGroup>

          <InputGroup>
            <InputLabel valid={!errors.donor_type} htmlFor="subscription_donor_type">
              {t('backend:donor_type')}
            </InputLabel>
            <Select
              {...register('donor_type')}
              id="subscription_donor_type"
              aria-invalid={!!errors.donor_type}
            >
              {donorTypes.map((donorType) => (
                <option value={donorType} key={donorType}>
                  {t(`backend:donor_types.${donorType}`)}
                </option>
              ))}
            </Select>
            <ErrorMessage attribute={t('backend:donor_type')} error={errors.donor_type }/>
          </InputGroup>

          <InputGroup>
            <InputLabel valid={!errors.company_name} htmlFor="subscription_company_name">
              {t('backend:company_name')}
            </InputLabel>
            <Input
              {...register('company_name')}
              id="subscription_company_name"
              aria-invalid={!!errors.company_name}
            />
            <ErrorMessage error={errors.company_name} attribute={t('backend:company_name')} />
          </InputGroup>

          <InputGroup>
            <InputLabel htmlFor="subscription_phone">
              {t('backend:phone_number')}
            </InputLabel>
            <Controller
              control={control}
              name="phone"
              render={({ field: { onChange, onBlur, value } }) => (
                <PhoneNumberInput
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  country={subscription.country}
                  id="subscription_phone"
                  ariaInvalid={!!errors.phone}
                />
              )}
            />
            <ErrorMessage error={errors.phone} attribute={t('backend:phone_number')} />
          </InputGroup>

          <AddressInputs form={form} required={false} />

          <InputGroup className="space-y-2">
            <CheckboxLabel htmlFor="subscription_email_opt_in">
              <Checkbox
                {...register('email_opt_in')}
                id="subscription_email_opt_in"
                className="mt-1"
              />
              <span>
                {t('backend:email_opt_in_text', { charity: charity.title })}
              </span>
            </CheckboxLabel>

            <CheckboxLabel htmlFor="subscription_phone_opt_in">
              <Checkbox
                {...register('phone_opt_in')}
                id="subscription_phone_opt_in"
                className="mt-1"
              />
              <span>
                {t('backend:phone_opt_in_text', { charity: charity.title })}
              </span>
            </CheckboxLabel>
          </InputGroup>
        </div>
        <div className="text-right">
          <Button
            type="submit"
            loading={loading}
            className="ml-auto"
          >
            {t('backend:save')}
          </Button>
        </div>
      </Card>
    </form>
  );
}
